import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import type { DurationRange, SortKey } from "@/types/video";
import { filterByDuration, getHotVideos, searchVideos, sortVideos } from "@/utils/video";
import FilterBar from "@/components/feature/FilterBar";
import Pagination from "@/components/feature/Pagination";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/feature/EmptyState";

const PAGE_SIZE = 8;
const hotKeywords = ["美食", "旅行", "宠物", "音乐", "运动", "科技", "收纳", "吉他"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(q);
  const [sort, setSort] = useState<SortKey>("newest");
  const [duration, setDuration] = useState<DurationRange>("all");
  const [page, setPage] = useState(1);

  useSEO({
    title: q ? `搜索“${q}” - 糖豆视频` : "搜索视频 - 糖豆视频",
    description: q
      ? `在糖豆视频搜索“${q}”的相关短视频，共找到相关内容，支持按上传时间、播放量排序与时长筛选。`
      : "在糖豆视频按关键词搜索短视频，支持标题精确与模糊查找，快速找到你想看的内容。",
    keywords: q ? `${q},${q}视频,搜索,糖豆视频` : "视频搜索,糖豆视频,短视频",
  });

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (keyword.trim() !== q) {
        setSearchParams(keyword.trim() ? { q: keyword.trim() } : {}, { replace: true });
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [keyword, q, setSearchParams]);

  const results = useMemo(() => {
    if (!q) return [];
    return filterByDuration(sortVideos(searchVideos(q), sort), duration);
  }, [q, sort, duration]);

  useEffect(() => {
    setPage(1);
  }, [q, sort, duration]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const items = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hotVideos = getHotVideos(4);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(keyword.trim() ? { q: keyword.trim() } : {}, { replace: true });
  };

  return (
    <div className="w-full">
      <section className="w-full px-4 md:px-8 pt-8 pb-6 bg-background-100 border-b border-background-200">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
          {q ? (
            <>
              搜索“<span className="text-primary-700">{q}</span>”
            </>
          ) : (
            "搜索视频"
          )}
        </h1>
        <form onSubmit={submit} className="mt-4 flex items-center gap-2 max-w-2xl">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="输入视频名称、作者或分类关键词…"
              className="w-full pl-10 pr-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
            />
          </div>
          <button
            type="submit"
            className="px-5 py-2.5 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </form>
        <div className="mt-4 flex items-center flex-wrap gap-2 text-sm text-foreground-500">
          <span>热门搜索：</span>
          {hotKeywords.map((kw) => (
            <Link
              key={kw}
              to={`/search?q=${encodeURIComponent(kw)}`}
              className="px-3 py-1 rounded-full bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors"
            >
              {kw}
            </Link>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8">
        {q ? (
          <>
            <div className="flex flex-col gap-4 mb-8">
              <p className="text-sm text-foreground-500">
                共找到 <span className="text-primary-700 font-medium">{results.length}</span> 个与“{q}”相关的视频
              </p>
              <FilterBar
                sort={sort}
                onSortChange={setSort}
                duration={duration}
                onDurationChange={setDuration}
              />
            </div>

            {items.length === 0 ? (
              <div>
                <EmptyState
                  icon="ri-search-eye-line"
                  title={`没有找到与“${q}”相关的视频`}
                  description="换个关键词试试，或者看看下面的热门推荐，说不定就有你感兴趣的。"
                  actionLabel="返回首页"
                  actionTo="/"
                />
                <div className="mt-4">
                  <h2 className="flex items-center gap-2 text-lg font-heading font-bold text-foreground-950 mb-5">
                    <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
                    热门推荐
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-x-6 md:gap-y-8">
                    {hotVideos.map((video) => (
                      <VideoCard key={video.id} video={video} showCategory />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-x-6 md:gap-y-8">
                  {items.map((video) => (
                    <VideoCard key={video.id} video={video} showDate showCategory />
                  ))}
                </div>
                <div className="mt-10">
                  <Pagination page={page} totalPages={totalPages} onChange={setPage} />
                </div>
              </>
            )}
          </>
        ) : (
          <EmptyState
            icon="ri-search-line"
            title="输入关键词开始搜索"
            description="试试搜索视频名称、上传作者或者感兴趣的分类，比如“美食”“旅行”“宠物”。"
          />
        )}
      </section>
    </div>
  );
}